import { ProblemDetails } from "../models/Http";
import {
  Product,
  ProductWriteModel,
  ProductId,
  SpecificProductStock,
  ProductSpoilageReason,
  ProductSyncResult
} from "../models/Product";
import { uri } from "../uri";
import { get, put, post, del } from "./api";
import { MinimalStoreInventoryItem } from "../models/StoreInventory";
import { Result, err, ok, Unit, isOk } from "../result";
import { ProductSyncFilters } from "../models/Filters";
import { ProductDeliveryReport } from "../models/ProductDelivery";
import { ImageInformation } from "../models/ImageInformation";

interface LookupData {
  product: Product;
  imageData?: string;
  rawDocument: string;
}

export interface LookupResult {
  identifier: string;
  product: Product;
  imageData: ImageInformation;
  rawDocument: string;
}

export const listV2 = async (): Promise<
  Result<ProblemDetails, ReadonlyArray<Product>>
> => {
  const response = await get("/v2/products");
  const content = await response.json();

  if (response.ok) {
    return ok(content as ReadonlyArray<Product>);
  }

  return err(content as ProblemDetails);
};

export const getSingleV2 = async (
  id: ProductId
): Promise<Result<ProblemDetails, Product>> => {
  const response = await get(`/v2/products/${id}`);
  const content = await response.json();

  if (response.ok) {
    return ok(content as Product);
  }

  return err(content as ProblemDetails);
};

export const lookup = async (
  gtin: string
): Promise<Result<ProblemDetails, any>> => {
  const response = await get("/v1/products/lookup", { gtin });

  if (response.status == 204) {
    return ok({});
  }

  if (response.status == 200) {
    const content = await response.json();
    const lookupData = content as LookupData;

    return ok({
      ...lookupData,
      identifier: gtin,
      imageData: toImageData(lookupData.imageData)
    });
  }

  return err((await response.json()) as ProblemDetails);
};

export const uploadImage = async (
  productId: ProductId,
  image: Blob
): Promise<Result<ProblemDetails, Product>> => {
  const response = await upload(`/v1/products/${productId}/image`, image);
  const content = await response.json();

  if (response.ok) {
    const product = content as Product;
    return ok(product);
  }

  return err(content as ProblemDetails);
};

export const register = async (
  product: Product,
  image?: Blob
): Promise<Result<ProblemDetails, any>> => {
  const response = await post("/v1/products", product);

  if (!response.ok) {
    const content = await response.json();
    return err(content as ProblemDetails);
  }

  const result = await response.json();
  return ok(result);
};

export const getSingleByGTIN = async (
  gtin: string
): Promise<Result<ProblemDetails, Product>> => {
  const response = await get(`/v1/products/${gtin}`);
  const content = await response.json();

  if (response.ok) {
    return ok(content as Product);
  }

  return err(content as ProblemDetails);
};

export const save = async (
  id: ProductId,
  product: ProductWriteModel
): Promise<Result<ProblemDetails, Product>> => {
  const response = await put(`/v1/products/${id}`, product);
  const content = await response.json();

  if (response.ok) {
    return ok(content as Product);
  }

  return err(content as ProblemDetails);
};

export const update = async (
  tenantInventoryItemId: string,
  storeId: string,
  product: any
): Promise<Result<ProblemDetails, any>> => {
  const response = await put(
    `/v2/products/${tenantInventoryItemId}?storeId=${storeId}`,
    product
  );

  if (!response.ok) {
    const content = await response.json();
    return ok(content as Product);
  }

  return ok({});
};

export const getStock = async (
  product: Product
): Promise<
  Result<ProblemDetails, ReadonlyArray<MinimalStoreInventoryItem>>
> => {
  const response = await get(`/v1/products/${product.id}/stores`);
  const content = await response.json();

  if (response.ok) {
    const list = content as ReadonlyArray<SpecificProductStock>;
    const mapped =
      list && list.length > 0
        ? list.map(x => toStoreInventoryItem(product, x))
        : [];

    return ok(mapped);
  }

  return err(content as ProblemDetails);
};

export const getSpoilageReasons = async (): Promise<
  Result<ProblemDetails, ReadonlyArray<ProductSpoilageReason>>
> => {
  const response = await get("/v1/products/spoilage-reasons");
  const content = await response.json();

  if (response.ok) {
    return ok(content as ReadonlyArray<ProductSpoilageReason>);
  }

  return err(content as ProblemDetails);
};

export const remove = async (
  tenantInventoryItemId: string
): Promise<Result<ProblemDetails, any>> => {
  const response = await del(`/v2/products/${tenantInventoryItemId}`);
  if (!response.ok) {
    const content = await response.json();
    return err(content as ProblemDetails);
  }
  return ok({});
};

export const getProductSyncDocumentUrl = (
  filters?: ProductSyncFilters
): string => {
  let queryString = "";

  if (filters) {
    queryString += `&hideMaxOnShelf=${filters.hideMaxOnShelf}`;
    queryString += `&hideOrderThreshold=${filters.hideOrderThreshold}`;
    queryString += `&hideArticleNumber=${filters.hideArticleNumber}`;
    queryString += `&hideLatestPurchase=${filters.hideLatestPurchase}`;
    queryString += `&productFilter=${filters.productFilter}`;

    if (
      filters.storeIds &&
      filters.storeIds.length &&
      filters.storeIds.length > 0
    )
      filters.storeIds.forEach(val => {
        queryString += `&storeIds=${val}`;
      });

    if (
      filters.categoryIds &&
      filters.categoryIds.length &&
      filters.categoryIds.length > 0
    )
      filters.categoryIds.forEach(val => {
        queryString += `&categoryIds=${val}`;
      });

    if (
      filters.priorityIds &&
      filters.priorityIds.length &&
      filters.priorityIds.length > 0
    )
      filters.priorityIds.forEach(val => {
        queryString += `&priorityIds=${val}`;
      });
  }

  return `/v1/sync/products/excel?1${queryString}`;
};

export const uploadProductSyncDocument = async (
  dryRun: boolean,
  file: Blob,
  selectedStores: []
): Promise<Result<ProblemDetails, ProductSyncResult>> => {
  const response = await upload(
    `/v1/sync/products-csv?dryRun=${dryRun}&storeIds=${selectedStores.join(
      ","
    )}`,
    file
  );

  const content = await response.json();

  if (response.ok) {
    return ok(content as ProductSyncResult);
  }

  return err(content as ProblemDetails);
};

export const uploadProductPriceUpdateDocument = async (
  file: Blob
): Promise<Result<ProblemDetails, ProductSyncResult>> => {
  const response = await upload(`/v1/sync/products/prices`, file);
  const content = await response.json();

  if (response.ok) {
    return ok(content as ProductSyncResult);
  }

  return err(content as ProblemDetails);
};

export const reportDelivery = async (
  delivery: ProductDeliveryReport
): Promise<Result<ProblemDetails, any>> => {
  const response = await post(`/v1/products/delivery`, delivery);

  if (response.ok) {
    return ok({});
  }

  const content = await response.json();

  return err(content as ProblemDetails);
};

export const deleteDelivery = async (
  deliveryId: string
): Promise<Result<ProblemDetails, any>> => {
  const response = await del(`/v1/products/delivery/${deliveryId}`);

  if (response.ok) {
    return ok({});
  }

  const content = await response.json();

  return err(content as ProblemDetails);
};

const upload = (
  url: string,
  data: Blob,
  name = "file",
  params = {},
  filename?: string
) => {
  const body = new FormData();

  body.append(name, data, filename);

  const init: RequestInit = {
    method: "POST",
    body: body
  };

  return fetch(uri(url, params), init);
};

export const getGTIN = async (): Promise<Result<ProblemDetails, string>> => {
  const response = await get(`/v1/products/gtin`);
  const content = await response.json();

  if (response.ok) {
    return ok(content as string);
  }

  return err(content as ProblemDetails);
};

const toImageData = (data?: string): ImageInformation => {
  return {
    url: data || "",
    blob: dataUrlToBlob(data)
  };
};

const dataUrlToBlob = (value?: string): Blob | undefined => {
  if (!value) return undefined;

  const matches = /^data:([a-z-]+\/[a-z-]+);base64,(.+)$/i.exec(value);

  if (matches === null || matches.length !== 3) {
    throw new Error(
      "Cannot convert string to Blob because it does not match the format for a data URL."
    );
  }

  const mimeType = matches[1];
  const data = atob(matches[2]);

  const bytes = new Uint8Array(data.length);

  for (let i = 0; i < bytes.length; ++i) {
    bytes[i] = data.charCodeAt(i);
  }

  return new Blob([bytes], {
    type: mimeType
  });
};

export const syncWithPos = async (
  selectedStores: []
): Promise<Result<ProblemDetails, Unit>> => {
  const response = await post(
    `/v1/sync/sync-with-pos?storeIds=${selectedStores.join(",")}`
  );

  const content = await response.json();

  if (response.ok) {
    return ok(content);
  }

  return err(content as ProblemDetails);
};

const toStoreInventoryItem = (
  product: Product,
  from: SpecificProductStock
): MinimalStoreInventoryItem => ({
  created: new Date(),
  exists: from.exists,
  isOrderable: from.isOrderable,
  hasPriceTag: from.hasPriceTag,
  id: "00000000-0000-0000-0000-000000000000",
  productId: product.id,
  gtin: product.gtin,
  productName: product.name,
  brandName: product.brandName,
  price: from.price,
  inStorage: from.inStorage,
  maxOnShelf: from.maxOnShelf,
  onShelf: from.onShelf,
  onShelfWorth: 0,
  orderThreshold: from.orderThreshold,
  orderUnit: from.orderUnit,
  articleNumber: from.articleNumber,
  priority: from.priority,
  storeId: from.storeId,
  storeName: from.storeName,
  updated: new Date()
});
